import { FC } from 'react';

import { Document, Page, Text, View, Image } from '@react-pdf/renderer';

import CoverPage from '@/assets/images/coverpage.png';
import { SimReportMetricType } from '@/constants';
import { Theme } from '@/theme/index';
import { SimulationResult, SimulationResultDetails } from '@/types/simulations';

import MetricSection from './MetricSection';
import { styles } from './styles';

interface PDFDocumentProps {
  reportName: string;
  reportTitle: string;
  bestSimulation: SimulationResult;
  selectedMetric: string;
  selectedTimeFrame: string;
  chartImageData: string;
  tableImageData: string;
  theme: Theme;
  getMetricKey: (
    metric: string,
    timeFrame: string
  ) => keyof SimulationResultDetails;
}

const SimulationReportDocument: FC<PDFDocumentProps> = ({
  reportName,
  reportTitle,
  bestSimulation,
  selectedMetric,
  selectedTimeFrame,
  chartImageData,
  tableImageData,
  getMetricKey,
}) => {
  const formatMetricValue = (value: unknown): string => {
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === 'object' &&
      'value' in value[0]
    ) {
      return Number(value[0].value).toFixed(2) + '%';
    }

    return Number(value).toFixed(2) + '%';
  };

  return (
    <Document>
      {/* Cover Page */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.coverpage}>
        <View style={styles.coverWrapper}>
          <Image src={CoverPage} style={styles.coverImage} />
          <View style={styles.titleWrapper}>
            {reportTitle && (
              <Text style={styles.reportTitle}>{reportTitle}</Text>
            )}
            <Text style={styles.reportName}>{reportName}</Text>
          </View>
        </View>
      </Page>

      {/* Details Page */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Simulation Results</Text>
        </View>
        <View style={styles.section}>
          <Text style={[styles.text, styles.bold, styles.bestSimulationTitle]}>
            Best Simulation Details:
          </Text>
          <Text style={styles.text}>
            Simulation Name: {bestSimulation.name}
          </Text>
          <Text style={styles.text}>Status: {bestSimulation.status}</Text>
          <Text style={styles.text}>
            Completion Date:{' '}
            {new Date(bestSimulation.dateDone).toLocaleDateString('tr-TR')}
          </Text>

          <View style={styles.metricsContainer}>
            <Text style={[styles.text, styles.bold, styles.keyMetricsTitle]}>
              Key Metrics:
            </Text>
            <MetricSection
              type={selectedMetric as SimReportMetricType}
              bestSimulation={bestSimulation}
              formatMetricValue={formatMetricValue}
            />
            <View style={[styles.metricRow, styles.lastMetricRow]}>
              <View style={styles.metricLabelCell}>
                <Text style={[styles.text, styles.bold]}>
                  Selected Metric ({selectedTimeFrame}):
                </Text>
              </View>
              <View style={styles.metricValueCell}>
                <Text style={[styles.text, styles.bold]}>
                  {formatMetricValue(
                    bestSimulation.result[
                      getMetricKey(selectedMetric, selectedTimeFrame)
                    ]
                  )}
                </Text>
              </View>
            </View>
          </View>
        </View>
        <View style={styles.footer}>
          <Text style={styles.footerText}>{`Rapor İsmi: ${reportName}`}</Text>
          <Text style={styles.footerCenter}>RatioSIM</Text>
          <Text style={styles.footerText}>Page 2/4</Text>
        </View>
      </Page>

      {/* Metrics Table Page */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Key Metrics Tables</Text>
        </View>
        <View style={styles.section}>
          <View style={styles.chartContainer}>
            {tableImageData && (
              <Image src={tableImageData} style={styles.chartImage} />
            )}
          </View>
        </View>
        <View style={styles.footer}>
          <Text style={styles.footerText}>{`Rapor İsmi: ${reportName}`}</Text>
          <Text style={styles.footerCenter}>RatioSIM</Text>
          <Text style={styles.footerText}>Page 3/4</Text>
        </View>
      </Page>

      {/* Chart Page */}
      <Page size={{ width: 842, height: 473.5 }} style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Simulation Comparison Chart</Text>
        </View>
        <View style={styles.section}>
          <View style={styles.chartContainer}>
            {chartImageData && (
              <Image src={chartImageData} style={styles.chartImage} />
            )}
            <Text style={styles.chartCaption}>
              {`This chart compares the ${selectedMetric.toUpperCase()} values across all simulations.`}
            </Text>
          </View>
        </View>
        <View style={styles.footer}>
          <Text style={styles.footerText}>{`Rapor İsmi: ${reportName}`}</Text>
          <Text style={styles.footerCenter}>RatioSIM</Text>
          <Text style={styles.footerText}>Page 4/4</Text>
        </View>
      </Page>
    </Document>
  );
};

export default SimulationReportDocument;
